// src/components/fraud/FraudLogsTable.js
// Paginated table for the Fraud Logs page.
// 25 rows per page. All display logic lives here — no business logic.
// Columns: Time | Action | Score | Risk Level | User | Transaction | Destination | Reason | Phase | Status

import React, { useState, useCallback } from 'react';
import { Table } from 'react-bootstrap';
import { PAGE_SIZE } from '../../hooks/useFraudLogs';

// ─── Colour maps ──────────────────────────────────────────────────────────────
const ACTION_COLOR = {
  ALLOW: '#22C55E',
  LOG: '#D9FF1F',
  REVIEW: '#F59E0B',
  BLOCK: '#EF4444',
};
const RISK_COLOR = {
  normal: '#22C55E',
  elevated: '#F59E0B',
  highest: '#EF4444',
};

// ─── Inline copy hook ─────────────────────────────────────────────────────────
const useCopy = () => {
  const [copiedKey, setCopied] = useState(null);
  const copy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    });
  }, []);
  return { copiedKey, copy };
};

// ─── Presentational atoms ─────────────────────────────────────────────────────

const ActionBadge = ({ action }) => {
  const upper = (action || '').toUpperCase();
  const color = ACTION_COLOR[upper] || 'var(--text-secondary)';
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: upper === 'LOG' ? '#050014' : color,
        background: `${color}22`,
        border: `1px solid ${color}55`,
        borderRadius: 20,
        padding: '2px 8px',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {action || '—'}
    </span>
  );
};

const ScorePill = ({ score }) => {
  if (score === null || score === undefined)
    return <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</span>;
  const color = score >= 70 ? '#EF4444' : score >= 40 ? '#F59E0B' : '#22C55E';
  return (
    <span
      style={{
        fontWeight: 800,
        color,
        fontSize: 15,
        minWidth: 28,
        display: 'inline-block',
        textAlign: 'center',
      }}
    >
      {score}
    </span>
  );
};

const RiskBadge = ({ level }) => {
  const l = (level || '').toLowerCase();
  const color = RISK_COLOR[l] || 'var(--text-secondary)';
  if (!level) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color,
        background: `${color}18`,
        border: `1px solid ${color}44`,
        borderRadius: 20,
        padding: '2px 7px',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {level}
    </span>
  );
};

// Shortened ID with tooltip + copy
const IdCell = ({ value, copyKey, copiedKey, onCopy }) => {
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const short =
    value.length > 16
      ? `${value.substring(0, 8)}…${value.substring(value.length - 5)}`
      : value;
  const isCopied = copiedKey === copyKey;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <span
        title={value}
        style={{
          fontSize: 11,
          fontFamily: 'monospace',
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap',
          cursor: 'default',
        }}
      >
        {short}
      </span>
      <button
        title={isCopied ? 'Copied!' : `Copy: ${value}`}
        onClick={(e) => { e.stopPropagation(); onCopy(value, copyKey); }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '0 2px',
          cursor: 'pointer',
          color: isCopied ? '#22C55E' : 'var(--text-secondary)',
          fontSize: 11,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {isCopied ? '✓' : '⧉'}
      </button>
    </span>
  );
};

// Reason chips (compact — max 2 shown in table, see all in drawer)
const ReasonChips = ({ reasons }) => {
  if (!reasons || reasons.length === 0)
    return <span style={{ color: 'var(--text-secondary)', fontSize: 11, fontStyle: 'italic' }}>No reasons</span>;

  const visible = reasons.slice(0, 2);
  const extra = reasons.length - 2;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {visible.map((r, i) => (
        <span
          key={i}
          title={r}
          style={{
            fontSize: 10,
            color: '#F59E0B',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.28)',
            borderRadius: 4,
            padding: '1px 5px',
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {r}
        </span>
      ))}
      {extra > 0 && (
        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>+{extra} more</span>
      )}
    </div>
  );
};

// Format date compact
const fmtTime = (d) => {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── Skeleton rows ─────────────────────────────────────────────────────────────
const SkeletonRows = () =>
  Array.from({ length: 8 }).map((_, i) => (
    <tr key={i}>
      {[80, 60, 40, 70, 130, 110, 90, 100, 70, 60].map((w, j) => (
        <td key={j} className="transparent-header py-3">
          <div className="fraud-skeleton" style={{ width: w, height: 12, borderRadius: 4 }} />
        </td>
      ))}
    </tr>
  ));

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = () => (
  <tr>
    <td colSpan={10} className="transparent-header text-center py-5">
      <div style={{ color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
        <p style={{ fontSize: 14, marginBottom: 4 }}>No fraud logs match your filters</p>
        <small style={{ fontSize: 12 }}>Try adjusting or resetting the filter criteria</small>
      </div>
    </td>
  </tr>
);

// ─── Pagination controls ──────────────────────────────────────────────────────
const Pagination = ({ page, hasMore, onPrev, onNext, loading, total }) => (
  <div
    className="d-flex justify-content-between align-items-center mt-3 px-1"
    style={{ flexWrap: 'wrap', gap: 8 }}
  >
    <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
      Page {page} · {PAGE_SIZE} per page {total !== null ? `· ${total} shown` : ''}
    </small>
    <div className="d-flex gap-2">
      <button
        id="fraud-logs-prev-btn"
        onClick={onPrev}
        disabled={page <= 1 || loading}
        style={{
          background: page <= 1 ? 'rgba(255,255,255,0.04)' : 'var(--bg-surface)',
          border: '1px solid var(--divider)',
          borderRadius: 6,
          color: page <= 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
          padding: '5px 14px',
          cursor: page <= 1 ? 'default' : 'pointer',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        ← Prev
      </button>
      <button
        id="fraud-logs-next-btn"
        onClick={onNext}
        disabled={!hasMore || loading}
        style={{
          background: !hasMore ? 'rgba(255,255,255,0.04)' : 'var(--accent-lime)',
          border: 'none',
          borderRadius: 6,
          color: !hasMore ? 'var(--text-secondary)' : '#050014',
          padding: '5px 14px',
          cursor: !hasMore ? 'default' : 'pointer',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        Next →
      </button>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const COLUMNS = [
  'Time', 'Action', 'Score', 'Risk Level', 'User',
  'Transaction', 'Destination', 'Reasons', 'Phase', 'Status',
];

const FraudLogsTable = ({ logs, loading, page, hasMore, onPrev, onNext, onRowClick }) => {
  const [hovered, setHovered] = useState(null);
  const { copiedKey, copy } = useCopy();

  return (
    <div id="fraud-logs-table" className="theme-card rounded-3 p-4">
      <div className="table-responsive">
        <Table className="align-middle body-text-two mb-0" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col}
                  className="transparent-header"
                  style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', paddingBottom: 10 }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : logs.length === 0 ? (
              <EmptyState />
            ) : (
              logs.map((log, idx) => {
                const stripeRisk = log.stripeRiskData || {};
                const riskLevel = stripeRisk.riskLevel || stripeRisk.risk_level || '';
                return (
                  <tr
                    key={log._id || idx}
                    onClick={() => onRowClick(log)}
                    onMouseEnter={() => setHovered(idx)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      borderBottom: '1px solid var(--divider)',
                      cursor: 'pointer',
                      background: hovered === idx ? 'rgba(217,255,31,0.04)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Time */}
                    <td className="transparent-header py-3" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {fmtTime(log.createdAt)}
                    </td>

                    {/* Action */}
                    <td className="transparent-header py-3">
                      <ActionBadge action={log.action} />
                    </td>

                    {/* Score */}
                    <td className="transparent-header py-3">
                      <ScorePill score={log.score} />
                    </td>

                    {/* Risk Level */}
                    <td className="transparent-header py-3">
                      <RiskBadge level={riskLevel} />
                    </td>

                    {/* User UID */}
                    <td className="transparent-header py-3">
                      <IdCell
                        value={log.uid || log.userEmail}
                        copyKey={`uid-${idx}`}
                        copiedKey={copiedKey}
                        onCopy={copy}
                      />
                    </td>

                    {/* Transaction ID */}
                    <td className="transparent-header py-3">
                      <IdCell
                        value={log.transactionId}
                        copyKey={`txn-${idx}`}
                        copiedKey={copiedKey}
                        onCopy={copy}
                      />
                    </td>

                    {/* Destination */}
                    <td className="transparent-header py-3" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                      {log.destination || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </td>

                    {/* Reasons */}
                    <td className="transparent-header py-3">
                      <ReasonChips reasons={log.reasons} />
                    </td>

                    {/* Phase */}
                    <td className="transparent-header py-3">
                      {log.phase ? (
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--text-secondary)',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid var(--divider)',
                            borderRadius: 4,
                            padding: '2px 6px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.phase}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="transparent-header py-3">
                      <ActionBadge action={log.status} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <Pagination
        page={page}
        hasMore={hasMore}
        onPrev={onPrev}
        onNext={onNext}
        loading={loading}
        total={logs.length}
      />
    </div>
  );
};

export default FraudLogsTable;
